import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { SAR } from "../components/RiyalIcon";
import { ArrowLeft, Car, ChevronLeft, Sparkles } from "lucide-react";
import ProductSheet from "../components/ProductSheet";
import ProductQuantityControl from "../components/ProductQuantityControl";
import { absoluteUrl } from "../components/ImageUpload";

export default function DemoMenu() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCategoryNavSticky, setIsCategoryNavSticky] = useState(false);
  const categoryButtonRefs = useRef(new Map());
  const categoryNavRef = useRef(null);
  const categoryScrollerRef = useRef(null);
  const isClickNavigatingRef = useRef(false);
  const sectionRefs = useRef(new Map());
  const scrollTargetRef = useRef(null);
  const scrollTargetTimerRef = useRef(null);
  const updateActiveSectionRef = useRef(() => {});
  const cart = useCart();

  useEffect(() => {
    api.get(`/demo/restaurant`).then((r) => {
      setData(r.data);
      cart.setRestaurant("__demo__");
      if (r.data.categories?.[0]) setActiveCat(r.data.categories[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!data?.categories?.length) return undefined;

    const updateActiveSection = () => {
      if (isClickNavigatingRef.current) return;
      const anchor = 92;
      const sectionPositions = data.categories
        .map((category) => {
          const section = sectionRefs.current.get(category.id);
          if (!section) return null;
          const rect = section.getBoundingClientRect();
          return { id: category.id, top: rect.top, bottom: rect.bottom };
        })
        .filter(Boolean);
      const nextSection = sectionPositions
        .filter((section) => section.top <= anchor && section.bottom > anchor)
        .sort((first, second) => second.top - first.top)[0]?.id
        || sectionPositions
          .filter((section) => section.top > anchor)
          .sort((first, second) => first.top - second.top)[0]?.id;

      if (!nextSection) return;
      if (scrollTargetRef.current && scrollTargetRef.current !== nextSection) return;

      scrollTargetRef.current = null;
      setActiveCat((current) => (current === nextSection ? current : nextSection));
    };
    updateActiveSectionRef.current = updateActiveSection;

    const observer = new IntersectionObserver(() => {
      updateActiveSection();
    }, {
      rootMargin: "-76px 0px -42% 0px",
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
    });

    data.categories.forEach((category) => {
      const section = sectionRefs.current.get(category.id);
      if (section) observer.observe(section);
    });

    return () => {
      observer.disconnect();
      updateActiveSectionRef.current = () => {};
    };
  }, [data]);

  useEffect(() => {
    if (!data) return undefined;

    const updateStickyState = () => {
      const navTop = categoryNavRef.current?.getBoundingClientRect().top;
      const nextState = navTop !== undefined && navTop <= 0;
      setIsCategoryNavSticky((current) => (current === nextState ? current : nextState));
    };

    updateStickyState();
    window.addEventListener("scroll", updateStickyState, { passive: true });

    return () => window.removeEventListener("scroll", updateStickyState);
  }, [data]);

  useEffect(() => {
    return () => window.clearTimeout(scrollTargetTimerRef.current);
  }, []);

  // Horizontal-only centering of the active pill: never touches page scroll.
  useEffect(() => {
    if (!activeCat) return;
    const scroller = categoryScrollerRef.current;
    const button = categoryButtonRefs.current.get(activeCat);
    if (!scroller || !button) return;
    const left = button.offsetLeft - (scroller.clientWidth - button.clientWidth) / 2;
    scroller.scrollTo({ left, behavior: "smooth" });
  }, [activeCat]);

  if (!data) return <div className="p-8 text-center text-gray-500">جاري التحميل…</div>;
  const { restaurant, categories, products } = data;

  const scrollToCategory = (categoryId) => {
    scrollTargetRef.current = categoryId;
    isClickNavigatingRef.current = true;
    window.clearTimeout(scrollTargetTimerRef.current);
    scrollTargetTimerRef.current = window.setTimeout(() => {
      isClickNavigatingRef.current = false;
      const targetSection = sectionRefs.current.get(categoryId);
      const targetRect = targetSection?.getBoundingClientRect();
      scrollTargetRef.current = null;
      if (targetRect && targetRect.bottom > 92 && targetRect.top < window.innerHeight) {
        setActiveCat(categoryId);
      } else {
        updateActiveSectionRef.current();
      }
    }, 700);
    setActiveCat(categoryId);
    sectionRefs.current.get(categoryId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#FAF8F4] pb-28">
      <div className="relative">
        <div className="h-52 bg-orange-200 overflow-hidden">
          {restaurant.hero_url && <img src={restaurant.hero_url} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        </div>
        <button onClick={() => nav("/demo")} className="absolute top-4 start-4 w-10 h-10 rounded-full bg-white/90 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={18} className="rotate-180" />
        </button>
        <div className="absolute top-4 end-4 pill bg-amber-500 text-white px-3 py-1 text-xs font-bold inline-flex items-center gap-1"><Sparkles size={12}/> نسخة تجريبية</div>
        <div className="max-w-md mx-auto -mt-14 relative px-5">
          <div className="bg-white rounded-3xl card-shadow p-5 border border-orange-100">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-extrabold" data-testid="restaurant-name">{restaurant.name}</h1>
                <p className="text-sm text-gray-600 mt-1">{restaurant.tagline}</p>
              </div>
              <div className="text-4xl">🥙</div>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1 text-green-700 font-bold">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block"/> مفتوح الآن
              </span>
              <span className="inline-flex items-center gap-1 text-gray-500"><Car size={12}/> استلام سيارة</span>
              <span className="text-gray-500">· ~{restaurant.prep_time_minutes || 15} د</span>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={categoryNavRef}
        className={[
          "sticky top-0 z-20 max-w-md mx-auto mt-6 px-5 pt-2 bg-[#FAF8F4]/95 backdrop-blur-sm",
          isCategoryNavSticky ? "shadow-[0_6px_12px_-10px_rgba(17,24,39,0.28)]" : "",
        ].filter(Boolean).join(" ")}
        data-testid="sticky-category-nav"
      >
        <div ref={categoryScrollerRef} className="flex gap-2 overflow-x-auto pb-2 -mx-5 px-5">
          {categories.map((c) => (
            <button
              key={c.id}
              ref={(node) => {
                if (node) categoryButtonRefs.current.set(c.id, node);
                else categoryButtonRefs.current.delete(c.id);
              }}
              onClick={() => scrollToCategory(c.id)}
              data-testid={`cat-${c.id}`}
              className={`pill whitespace-nowrap px-4 py-2 text-sm font-bold border ${
                activeCat === c.id
                  ? "brand-grad text-white border-transparent"
                  : "bg-white text-gray-800 border-orange-100"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 mt-4 space-y-8">
        {categories.map((category) => {
          const sectionProducts = products.filter((product) => product.category_id === category.id);

          return (
            <section
              key={category.id}
              ref={(node) => {
                if (node) sectionRefs.current.set(category.id, node);
                else sectionRefs.current.delete(category.id);
              }}
              className="scroll-mt-24 space-y-3 border-b border-orange-100/60"
              data-category-id={category.id}
              data-testid={`menu-section-${category.id}`}
            >
              <h2 className="font-extrabold text-2xl" data-testid={`section-title-${category.id}`}>
                {category.name}
              </h2>
              {sectionProducts.map((p, productIndex) => (
                <div
                  key={p.id}
                  className={[
                    "px-3 py-[5px] flex gap-3 flex-row-reverse cursor-pointer",
                    productIndex < sectionProducts.length - 1 ? "border-b border-orange-100/60" : "",
                    (p.option_groups || []).length > 0 ? "h-[180px]" : "h-[170px]",
                  ].filter(Boolean).join(" ")}
                  onClick={() => setSelectedProduct(p)}
                  data-testid={`product-${p.id}`}
                >
                  <div
                    className={`shrink-0 ${
                      (p.option_groups || []).length > 0
                        ? "h-[170px] w-[150px]"
                        : "mt-[5px] h-[150px] w-[150px]"
                    }`}
                    dir="ltr"
                  >
                    <div className="relative h-[150px] w-[150px]">
                      <div className="customer-menu-image-frame w-full h-full rounded-2xl overflow-hidden food-img">
                        {p.image_url ? (
                          <img src={absoluteUrl(p.image_url)} alt="" className="w-full h-full object-contain" />
                        ) : (
                          <div className="w-full h-full grid place-items-center text-[55px]">🍽️</div>
                        )}
                      </div>
                      <DemoProductListAction
                        product={p}
                        cart={cart}
                        onOpenDetails={() => setSelectedProduct(p)}
                      />
                    </div>
                    {(p.option_groups || []).length > 0 && (
                      <div className="mt-1 text-center text-xs font-medium text-gray-500"
                        data-testid={`customize-${p.id}`} dir="rtl">
                        تخصيص
                      </div>
                    )}
                  </div>
                  <div className="min-h-0 h-full flex-1 overflow-hidden text-right">
                    <div className="flex h-full flex-col justify-between">
                      <div className="space-y-1">
                        <div className="line-clamp-2 font-extrabold text-base">{p.name}</div>
                        <div className="line-clamp-2 text-sm text-gray-600"
                          data-testid={`product-description-${p.id}`}>
                          {p.description?.trim() || p.name}
                        </div>
                      </div>
                      <div className="mt-0.5 text-xl font-bold text-black" data-testid={`product-price-${p.id}`}>
                        <SAR>{p.price}</SAR>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {sectionProducts.length === 0 && (
                <div className="text-center text-gray-500 py-6">لا توجد أصناف في هذا القسم</div>
              )}
            </section>
          );
        })}
      </div>

      {cart.count > 0 && (
        <div className="fixed inset-x-0 z-30" style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0px))" }}>
          <div className="max-w-md mx-auto p-4">
            <Link
              to="/demo/checkout"
              data-testid="view-cart"
              className="flex items-center gap-2 pill brand-grad text-white px-5 py-3.5 tap-target card-shadow"
            >
              <span className="inline-flex shrink-0 self-center items-center gap-2">
                <span
                  className={
                    "grid h-6 w-6 place-items-center rounded-full border border-white/70 "
                    + "bg-[#EA580C] text-xs font-extrabold leading-none num-badge"
                  }
                  data-testid="cart-bar-count"
                >
                  <span className="cart-bar-count-value">{cart.count}</span>
                </span>
                <span className="inline-flex h-6 self-center items-center font-extrabold leading-none"
                  data-testid="cart-bar-total">
                  <SAR className="cart-bar-price-value">{cart.total}</SAR>
                </span>
              </span>
              <span
                className={
                  "flex h-6 flex-1 self-center items-center justify-end text-left font-bold leading-none"
                }
                data-testid="cart-bar-label"
              >
                <span className="cart-bar-label-value">عرض السلة</span>
              </span>
              <span className="grid h-6 w-6 shrink-0 self-center place-items-center rounded-full bg-white text-[#F97316]"
                data-testid="cart-bar-arrow">
                <ChevronLeft size={14} />
              </span>
            </Link>
          </div>
        </div>
      )}

      {selectedProduct && (
        <ProductSheet product={selectedProduct} onClose={() => setSelectedProduct(null)}
          onAdd={(line) => { cart.addCustomized(line); setSelectedProduct(null); }} />
      )}
    </div>
  );
}

function DemoProductListAction({ product, cart, onOpenDetails }) {
  const hasOptionGroups = (product.option_groups || []).length > 0;
  const cartLine = cart.items.find((item) => (
    item.product_id === product.id && (item.selected_addons || []).length === 0
  ));
  const quantity = cartLine?.quantity || 0;

  const handleIncrease = (event) => {
    event.stopPropagation();
    if (hasOptionGroups) {
      onOpenDetails();
      return;
    }
    cart.addItem(product);
  };

  const handleDecrease = (event) => {
    event.stopPropagation();
    if (cartLine) cart.setQty(cartLine._key, cartLine.quantity - 1);
  };

  return (
    <ProductQuantityControl
      quantity={hasOptionGroups ? 0 : quantity}
      onDecrease={handleDecrease}
      onIncrease={handleIncrease}
      placement="floating"
      containerTestId={`quantity-controls-${product.id}`}
      decreaseTestId={`decrease-${product.id}`}
      increaseTestId={`add-${product.id}`}
      quantityTestId={`quantity-${product.id}`}
      deleteIconTestId={`delete-icon-${product.id}`}
      minusIconTestId={`minus-icon-${product.id}`}
      increaseLabel={`إضافة ${product.name}`}
      decreaseLabel={`تقليل ${product.name}`}
      plusStrokeWidth={2.5}
    />
  );
}
